/**
 * Jumping to the message a chat-search result matched.
 *
 * Extracted from BirdChatPanel because it is self-contained and because the
 * three effects below only make sense read together — inlined among the panel's
 * other twenty hooks, their ordering constraints were invisible, and all three
 * of the bugs this file documents came from exactly that.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { BirdChatHandover } from '../../../services/events/birdChatEvents';

/** Which message in which conversation a chat-search result asked us to open. */
export interface DeepLinkTarget {
  readonly sessionId: string;
  readonly messageId: string;
}

/**
 * The ONE predicate for "is this handover a deep link?".
 *
 * Both halves are required. A message id without a session id cannot be acted
 * on — there is no conversation to open — and treating it as a deep link arms
 * a wait that nothing can ever satisfy. When the seeding check and the arming
 * check disagreed on this, a handover carrying only a message id suppressed
 * auto-scroll for the life of the panel.
 */
export function deepLinkTargetFrom(handover?: BirdChatHandover | null): DeepLinkTarget | null {
  if (!handover?.sessionId || !handover.messageId) return null;
  return { sessionId: handover.sessionId, messageId: handover.messageId };
}

/** How long a deep-link waits for its message before releasing auto-scroll. */
const DEEP_LINK_TIMEOUT_MS = 5000;

/** How long the matched message stays tinted after the jump. */
const DEEP_LINK_HIGHLIGHT_MS = 2000;

interface DeepLinkScroll {
  /** Message id to tint, or null. */
  readonly highlightedMessageId: string | null;
  /** True while a jump is pending — auto-scroll must stand down. */
  readonly isDeepLinkInFlight: () => boolean;
  /** Arm a jump (called when a handover carrying a target arrives). */
  readonly armDeepLink: (target: DeepLinkTarget) => void;
  /** Abandon any pending jump — the user navigated somewhere else. */
  readonly releaseDeepLink: () => void;
}

export function useDeepLinkScroll(
  handover: BirdChatHandover | null | undefined,
  messages: ReadonlyArray<{ id: string }>,
  containerRef: RefObject<HTMLDivElement | null>,
): DeepLinkScroll {
  // A chat-search result names the message it matched. Held until that
  // message has actually RENDERED, which is later than it looks.
  const deepLinkInFlightRef = useRef(deepLinkTargetFrom(handover) !== null);
  const [deepLinkTarget, setDeepLinkTarget] = useState<DeepLinkTarget | null>(() =>
    deepLinkTargetFrom(handover),
  );
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  /** Stop waiting, and let auto-scroll work again. Every exit path goes here. */
  const releaseDeepLink = useCallback(() => {
    deepLinkInFlightRef.current = false;
    setDeepLinkTarget(null);
  }, []);

  const armDeepLink = useCallback((target: DeepLinkTarget) => {
    deepLinkInFlightRef.current = true;
    setDeepLinkTarget(target);
  }, []);

  // A getter, not the boolean: the auto-scroll effect in the panel must read
  // this WITHOUT depending on it, or clearing the flag re-runs that effect and
  // it scrolls to the bottom, undoing the jump one commit after it lands.
  const isDeepLinkInFlight = useCallback(() => deepLinkInFlightRef.current, []);

  // Deep-link, part 1: scroll to the matched message once it is on screen.
  //
  // The condition is simply "is the target in `messages` yet?". It deliberately
  // does NOT consult `isLoadingHistory`, because that flag cannot answer the
  // question: loaded history reaches `messages` through an effect, so on the
  // commit where the query resolves, loading is already false while the DOM
  // still holds the rows from before. Treating "not loading" as "the target is
  // absent" gave up one commit before it could ever appear, every time. (A
  // cached session is worse still: loading is never true at all.)
  //
  // `switchSession` clears `messages` as of #2607, so those stale rows are now
  // an empty list rather than the previous conversation's — which changes what
  // is on screen during the gap, and nothing about why this effect must key on
  // the target's presence instead of on a loading flag.
  useEffect(() => {
    if (!deepLinkTarget) return;
    if (!messages.some((m) => m.id === deepLinkTarget.messageId)) return;

    // Scoped to this panel's list rather than the whole document — the panel
    // can be mounted alongside other surfaces carrying message nodes.
    const node = containerRef.current?.querySelector(
      `[data-message-id="${CSS.escape(deepLinkTarget.messageId)}"]`,
    );
    // In `messages` but not in the DOM yet (the panel renders a spinner while
    // history loads, whatever `messages` holds). Keep waiting rather than
    // declaring success — consuming the deep link here would leave the user
    // where they were, with a highlight burning on a row they cannot see.
    if (!node) return;

    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(deepLinkTarget.messageId);
    // Clear the TARGET here, but NOT the in-flight ref — that is released one
    // commit later, by the highlight effect below.
    //
    // Releasing it inline makes the outcome depend on effect ordering: this
    // hook's effects register before the panel's auto-scroll effect, so a
    // same-commit release means auto-scroll sees "not in flight" and
    // immediately scrolls to the bottom, undoing the jump it just made.
    // Deferring by a commit makes the suppression hold for whichever order
    // those two end up in, so moving this hook call cannot silently break it.
    setDeepLinkTarget(null);
  }, [deepLinkTarget, messages, containerRef]);

  // Deep-link, part 2: stop waiting eventually.
  //
  // A SEPARATE effect, keyed only on the target. Sharing the effect above meant
  // the timer was cleared and re-created on every `messages` identity change —
  // and `useAgentChat` replaces that array on every streamed token, so the
  // deadline could never elapse while a reply was arriving. A user who typed a
  // new question while a deep link was stuck therefore had auto-scroll
  // suppressed for the whole stream, watching the answer land below the fold.
  useEffect(() => {
    if (!deepLinkTarget) return undefined;
    const giveUp = window.setTimeout(releaseDeepLink, DEEP_LINK_TIMEOUT_MS);
    return () => window.clearTimeout(giveUp);
  }, [deepLinkTarget, releaseDeepLink]);

  // Clear the highlight on its own clock.
  //
  // Keyed on the highlight, not on the pending id. Hanging the timeout off the
  // deep-link effect meant its cleanup fired when that effect re-ran to clear
  // the pending id, cancelling the timer before it could ever elapse — so the
  // "brief" highlight stayed lit until the panel unmounted.
  useEffect(() => {
    if (!highlightedMessageId) return undefined;
    // The deferred release described above: by now the jump has painted, so
    // ordinary auto-scroll can resume.
    deepLinkInFlightRef.current = false;
    const timer = window.setTimeout(() => setHighlightedMessageId(null), DEEP_LINK_HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [highlightedMessageId]);
  return { highlightedMessageId, isDeepLinkInFlight, armDeepLink, releaseDeepLink };
}
